import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../css/App.css';
import ciraLogo from '../images/CIRA.png';
import noaaLogo from '../images/noaa_digital_logo.svg';
import ai2esLogo from '../images/ai2es.png';
import { usePlayFunc } from '../js/sliderUtils.js';
import { initFhourAdjust } from '../js/dateUtils.js';
import { handleGridModelChange, handleSelectChange } from '../handlers/handlers.js';

// --- UI Components ---
import ModelDropdowns from '../components/ModelDropdowns';
import VariableDropdowns from '../components/VariableDropdowns';
import MapDisplay from "../components/MapDisplay";
import TimeSliderControls from "../components/TimeSliderControls";
import WindControls from "../components/WindControls";
import ColorbarContainer from "../components/ColorbarContainer";

// --- Custom Hook Effects ---
import {
    useTimeZoneOptionsEffect,
    useInputValueEffect,
    useColorbarEffect,
    useTicksAndLabelsEffect,
    useInitializationOptionsEffect,
    useDateTextEffect,
    useMapSyncEffect,
    useAddDataEffect,
    useMapInitializeEffect,
    useHomeEffect,
    useCountyEffect,
    useBorderEffect,
    useHurricaneEffect
} from '../hooks/useHomeEffects';

function mapModel(selectedModel) {
  if (!selectedModel) return '';

  const map = {
    au_ifs: 'EAUR',
    au_gfs: 'NAUR',
    pw_ifs: 'EPNG',
    pw_gfs: 'NPNG',
    gc_ifs: 'EGRP',
    gc_gfs: 'NGRP',
    fcnv2_ifs: 'EFOR',
    fcnv2_gfs: 'NFOR',
    gfs_gfs: 'GFS',
    ifs_ifs: 'IFS', 
    aifs_aifs: 'AIFS',
  };

  return map[selectedModel] || '';
}

/**
 * Main Home component
 * 
 * This is the top-level UI layout and logic handler for the map-based visualization.
 * It controls:
 *  - Model & variable dropdowns
 *  - Time slider
 *  - Map initialization
 *  - Synchronization of multiple maps
 *  - Data layer loading
 *  - UI display options
 */
const Home = () => {

    // ===== Map & UI State =====

    const [mapInitialized, setMapInitialized] = useState(false);  // Whether the map(s) are initialized
    const [compareBool, setCompareBool] = useState(false);        // Whether comparison mode is enabled
    const [lockState, setLockState] = useState(false);            // Whether views are locked in comparison mode

    // Selected models and variables
    const [selectedModel, setSelectedModel] = useState('gc_gfs');  // Single map model
    const [gridModels, setGridModels] = useState(["gfs_gfs", "aifs_aifs", "gc_gfs", "pw_gfs"]);  // Models in comparison mode
    const [selectedVariable, setSelectedVariable] = useState('msl');
    const [selectedVarClass, setSelectedVarClass] = useState('Pressure');
    const [initModel] = useState("gfs");
    const selectedDeckModel = mapModel(selectedModel);
    const [indexData, setIndexData] = useState(null);
    
    // Initialization & forecast hour
    const [selectedInit, setSelectedInit] = useState("20260420_00");
    const [initializationOptions, setInitializationOptions] = useState([]);
    const [fhour, setFhour] = useState("000");               // Forecast hour
    const [inputValue, setInputValue] = useState(fhour);     // Text input forecast hour
    const [play, setPlay] = useState(false);                 // Animation play/pause state
    const prevSelectedInitRef = useRef();                    // Previous initialization (for slider adjustment)

    // Time & timezone state
    const [timeZone, setTimeZone] = useState('UTC');
    const [timeZoneOptions, setTimeZoneOptions] = useState([]);
    const [initstr, setInitstr] = useState();   // Init timestamp string
    const [timing, setTiming] = useState();     // Time offset string
    const [validstr, setValidstr] = useState(); // Valid time string

    // Map references
    const popupRef = useRef(null);
    const singleMapRef = useRef(null);
    const griddedMapRefs = useRef([null, null, null, null]);

    // UI Display toggles
    const [colorMinimized, setColorMinimized] = useState(false);  // Collapse colorbar
    const [timeMinimized, setTimeMinimized] = useState(false);    // Collapse time slider

    // Map display settings
    const [countyState, setCountyState] = useState(true)
    const [borderState, setBorderState] = useState(true)
    const [windState, setWindState] = useState(false);    // Winds toggle
    const [hurricaneState, setHurricaneState] = useState(false); // Hurricane tracks toggle
    // added for hurricane
    //const [hurricaneModelAvailable, setHurricaneModelAvailable] = useState(true);
    const [hurricaneModelAvailable, setHurricaneModelAvailable] = useState([true, true, true, true]);
    const [hurricaneTrackMode, setHurricaneTrackMode] = useState('selected');
    const [hurricaneAvailable, setHurricaneAvailable] = useState(false);
    const [hurricaneYear, setHurricaneYear] = useState('');
    const [hurricaneCycle, setHurricaneCycle] = useState('');
    const [hurricaneRadii34State, setHurricaneRadii34State] = useState(false);
    const [hasWindRadii34, setHasWindRadii34] = useState(false);
    //added 61826
    const [hurricaneModelMode, setHurricaneModelMode] = useState("selected");
    const [selectedStormIds, setSelectedStormIds] = useState([]);
    const [selectedModelStorms, setSelectedModelStorms] = useState([]);
    const [allModelStorms, setAllModelStorms] = useState([]);
    const [hurricaneRows, setHurricaneRows] = useState([]);
    
    const [sliderLev, setSliderLev] = useState(0);        // Vertical slider state
    const [sliderPresLev, setSliderPresLev] = useState("10");  // Pressure level slider state
    const [cmapSpacing, setCmapSpacing] = useState(null);      // Colormap tick spacing

    // Colormap data
    const [, setVariableLevels] = useState({});
    const [, setColormapColors] = useState({});

    // ===== Handlers =====

    // Hurricane helper - grab init time(s)
    function parseInitToHurricaneCycle(initValue) {
      const s = String(initValue || '').trim();

      if (!/^\d{8}_\d{2}$/.test(s)) {
        return { year: '', cycle: '' };
      }

      const year = s.slice(0, 4);
      const mmddhh = s.slice(4, 8) + s.slice(9, 11);

      return {
        year,
        cycle: mmddhh,
      };
    }

    const resetHurricaneForModelChange = (index) => {
      setSelectedStormIds([]);
      setHurricaneRows([]);

      const map = griddedMapRefs?.current?.[index];
     
      if (map) {
        // remove only this panel's hurricane layers
        [
          "hurricane-lines",
          "hurricane-points",
          "hurricane-t0-labels",
          "hurricane-wind-radii-34-outline",
          "hurricane-wind-radii-34-fill",
        ].forEach((layerId) => {
          try {
            if (map.getLayer(layerId)) map.removeLayer(layerId);
          } catch {}
        });

        try {
          if (map.getSource("hurricane-tracks")) {
            map.removeSource("hurricane-tracks");
          }
        } catch {}
        
        map?.flyTo({
          center: [-95, 40],
          zoom: 2,
          duration: 1500,
        });
      }  
    };

    const onSelectChange = (e, type, varClass = null) => {
      const value = e.target.value;

      const hurricaneUnsupportedModels = [
        "gfs_gfs",
        "ifs_ifs",
        "aifs_aifs",
      ];

      if (type === "Model" && hurricaneUnsupportedModels.includes(value)) {
        setHurricaneState(false);
        setSelectedStormIds([]);
        setSelectedModelStorms([]);
        setAllModelStorms([]);
        setHurricaneRows([]);

        singleMapRef?.current?.flyTo({
          center: [-90, 40],
          zoom: 3,
          duration: 1500,
        });

        griddedMapRefs?.current?.forEach((map) => {
          map?.flyTo({
            center: [-95, 40],
            zoom: 2,
            duration: 1500,
          });
        });
      }

      handleSelectChange(e, type, varClass, {
        setSelectedModel,
        setCompareBool,
        setSelectedInit,
        selectedModel,
        initFhourAdjust,
        fhour,
        setFhour,
        prevSelectedInitRef,
        setSelectedVariable,
        setSelectedVarClass
      });
    };

    /**
     * Toggle colorbar collapse state
     */
    const toggleMinimizeColor = () => {
        setColorMinimized(!colorMinimized);
    };

    /**
     * Toggle time slider collapse state
     */
    const toggleMinimizeTime = () => {
        setTimeMinimized(!timeMinimized);
    };

    /**
     * Hide popup
     */
    const disablePopup = () => {
        if (popupRef.current) {
            try {
                popupRef.current._content.style.visibility = 'hidden';
            } catch {}
        }
    };

    /**
     * Show popup
     */
    const enablePopup = () => {
        if (popupRef.current) {
            try {
                popupRef.current._content.style.visibility = 'visible';
            } catch {}
        }
    };

    // ===== Hooks & Effects =====
    
    useEffect(() => {
      console.log('Selected model:', selectedModel);
    }, [selectedModel]);
    useEffect(() => {
      console.log('Selected variable:', selectedVariable);
      console.log('Selected var class:', selectedVarClass);
    }, [selectedVariable, selectedVarClass]);
    
    const USE_TEST_CYCLE = false; //was true for testing

    useEffect(() => {
      if (!selectedInit) return;

      let year, cycle;

      if (USE_TEST_CYCLE) {
        year = "2026";
        cycle = "042000";   // 👈 your test case
      } else {
        ({ year, cycle } = parseInitToHurricaneCycle(selectedInit));
      }

      console.log('INIT:', selectedInit);
      console.log('Parsed hurricane year:', year);
      console.log('Parsed hurricane cycle:', cycle);

      setHurricaneYear(year);
      setHurricaneCycle(cycle);

    }, [selectedInit]);

    useEffect(() => {
      async function loadIndex() {
        try {
          const res = await fetch("/geojsons/modelvar_avail_check/index.json");

          if (!res.ok) return;

          const data = await res.json();
          setIndexData(data);
        } catch (err) {
          console.error("Index load failed:", err);
        }
      }

      loadIndex();
    }, []);
    
    const availabilityModel =
      selectedModel === "compare"
        ? gridModels?.[0]
        : selectedModel;
     
    const availableVars = useMemo(() => {
      if (!indexData || !selectedInit || !availabilityModel) return [];

      return (
        indexData.cycles?.[selectedInit]?.models?.[availabilityModel]?.variables || []
      );
    }, [indexData, selectedInit, availabilityModel]);
    
    console.log("Availability model:", availabilityModel);
    console.log("Available vars:", availableVars);  

    const isVarAvailable = (id) => availableVars.includes(id);

    useEffect(() => {
      console.log("Selected init:", selectedInit);
      console.log("Selected model:", selectedModel);
      console.log("Available vars:", availableVars);
    }, [selectedInit, selectedModel, availableVars]);

    useEffect(() => {
      const setRadiiVisibility = (map) => {
        if (!map) return;

        const visibility = hurricaneRadii34State ? 'visible' : 'none';

        [
          'hurricane-wind-radii-34-fill',
          'hurricane-wind-radii-34-outline',
        ].forEach((layerId) => {
          if (map.getLayer(layerId)) {
            map.setLayoutProperty(layerId, 'visibility', visibility);
          }
        });
      };

      if (singleMapRef?.current) {
        setRadiiVisibility(singleMapRef.current);
      }

      if (griddedMapRefs?.current) {
        griddedMapRefs.current.forEach(setRadiiVisibility);
      }
    }, [hurricaneRadii34State, singleMapRef, griddedMapRefs]);
    
    useEffect(() => {
      if (!hurricaneYear || !hurricaneCycle) return;

      const checkHurricaneAvailability = async () => {
        try {
          const url = `${window.location.origin}/geojsons/hurricane/index/${hurricaneYear}/${hurricaneCycle}.json`;
          console.log('Checking hurricane availability:', url);

          const res = await fetch(url, { method: 'HEAD' });

          if (res.ok) {
            console.log('Hurricane data available');
            setHurricaneAvailable(true);
          } else {
            console.log('No hurricane data for selected init');
            setHurricaneAvailable(false);
            setHurricaneState(false);
          }
        } catch (err) {
          console.warn('Failed checking hurricane availability:', err);
          setHurricaneAvailable(false);
          setHurricaneState(false);
        }
      };

      checkHurricaneAvailability();
    }, [hurricaneYear, hurricaneCycle]);    

    useEffect(() => {
      if (hurricaneTrackMode === "selected") {
        setSelectedStormIds(selectedModelStorms.map((s) => s.storm_id));
      }

      if (hurricaneTrackMode === "all") {
        setSelectedStormIds(allModelStorms.map((s) => s.storm_id));
      }
    }, [hurricaneTrackMode, selectedModelStorms, allModelStorms]);

    // Play/pause animation logic
    usePlayFunc({
        fhour,
        setFhour,
        play,
        setPlay,
        selectedInit,
        selectedModel,
        selectedVariable,
        compareBool,
        griddedMapRefs,
        singleMapRef,
        popupRef,
        gridModels
    });

    // One-time setup: initializes sliders, init options, and fetches colormap configs
    useHomeEffect(
        sliderLev,
        setSliderLev,
        sliderPresLev,
        setSliderPresLev,
        fhour,
        setFhour,
        setInitializationOptions,
        setSelectedInit,
        prevSelectedInitRef,
        setVariableLevels,
        setColormapColors
    );

    // Timezone dropdown population
    useTimeZoneOptionsEffect(setTimeZoneOptions);

    // Set default selectedInit when initialization options are loaded
    useInitializationOptionsEffect(initializationOptions, selectedInit, setSelectedInit);

    // Keep inputValue synced with forecast hour
    useInputValueEffect(fhour, setInputValue);

    // Redraw colorbar when variable changes
    useColorbarEffect(selectedVariable, cmapSpacing, setCmapSpacing);

    // Draw tick labels when colorbar spacing is ready
    useTicksAndLabelsEffect(selectedVariable, cmapSpacing);

    // Update init/valid/offset strings when relevant state changes
    useDateTextEffect(selectedInit, timeZone, fhour, setInitstr, setTiming, setValidstr);

    // Synchronize comparison map views when locked
    useMapSyncEffect(lockState, griddedMapRefs);

    useCountyEffect(countyState,singleMapRef,griddedMapRefs);

    useBorderEffect(borderState,singleMapRef,griddedMapRefs)

    useHurricaneEffect(hurricaneState, hurricaneYear, hurricaneCycle, selectedModel, hurricaneTrackMode, setHurricaneModelAvailable, hurricaneRadii34State, setHasWindRadii34, singleMapRef, griddedMapRefs, mapInitialized, compareBool, gridModels, setSelectedModelStorms, setAllModelStorms, selectedStormIds, setSelectedStormIds, hurricaneRows, setHurricaneRows); //, selectedStormIds);

    // Add new data layers when model/variable/timestep changes
    useAddDataEffect(
        compareBool,
        griddedMapRefs,
        gridModels,
        selectedInit,
        selectedModel,
        selectedVariable,
        fhour,
        singleMapRef,
        popupRef,
        sliderPresLev,
        windState,
        initModel,
        mapInitialized,
        borderState
    );
    
    // Initialize maps when switching between single & comparison mode
    useMapInitializeEffect(compareBool, singleMapRef, griddedMapRefs, popupRef, setMapInitialized, countyState);

    // ===== Render =====
    
    return (
        <div className="bodystyle">
            <div id="everything">
                <div id="MenuMap">

                    {/* --- Left Sidebar --- */}
                    <div id="LeftMenu">
                        <ModelDropdowns 
                            selectedModel={selectedModel}
                            selectedInit={selectedInit}
                            initializationOptions={initializationOptions}
                            onSelectChange={onSelectChange}
                        />
                        <VariableDropdowns
                            selectedVarClass={selectedVarClass}
                            selectedVariable={selectedVariable}
                            onSelectChange={onSelectChange}
                            isVarAvailable={isVarAvailable}
                            selectedModel={selectedModel}
                        />
                        <WindControls
                            windState={windState}
                            setWindState={setWindState}
                            countyState={countyState}
                            setCountyState={setCountyState}
                            borderState={borderState}
                            setBorderState={setBorderState}
                            sliderPresLev={sliderPresLev}
                            setSliderPresLev={setSliderPresLev}
                            hurricaneState={hurricaneState}
                            setHurricaneState={setHurricaneState}
                            // added for Hurricane
                            hurricaneModelAvailable={hurricaneModelAvailable}
                            hurricaneAvailable={hurricaneAvailable}
                            hurricaneYear={hurricaneYear}
                            setHurricaneYear={setHurricaneYear}
                            hurricaneCycle={hurricaneCycle}
                            setHurricaneCycle={setHurricaneCycle}
                            hurricaneTrackMode={hurricaneTrackMode}
                            setHurricaneTrackMode={setHurricaneTrackMode}
                            selectedModelStorms={selectedModelStorms}
                            allModelStorms={allModelStorms}
                            hurricaneRadii34State={hurricaneRadii34State}
                            setHurricaneRadii34State={setHurricaneRadii34State}
                            hasWindRadii34={hasWindRadii34}
                            selectedDeckModel={selectedDeckModel}
                            selectedStormIds={selectedStormIds}
                            setSelectedStormIds={setSelectedStormIds}
                            compareBool={compareBool}
                            gridModels={gridModels}
                            singleMapRef={singleMapRef}
                            griddedMapRefs={griddedMapRefs}
                        />
                        <div className="sidebar-footer">
                            <div className="sidebar-logos">
                                <a href="https://www.cira.colostate.edu/" target="_blank" rel="noreferrer"><img src={ciraLogo} alt="CIRA" /></a>
                                <a href="https://www.noaa.gov/" target="_blank" rel="noreferrer"><img src={noaaLogo} alt="NOAA" /></a>
                                <a href="https://www.ai2es.org/" target="_blank" rel="noreferrer"><img src={ai2esLogo} alt="AI2ES" /></a>
                            </div>
                            <div className="sidebar-disclaimer">
                                Real-time AI weather model visualizations.{' '}
                                <a href="https://rammb.cira.colostate.edu/resources/experimental_products_disclaimer.asp" target="_blank" rel="noreferrer">
                                    Products are experimental.
                                </a>
                            </div>
                            <hr className="sidebar-divider" />
                            <div className="sidebar-nav-links">
                                <a href="#/faq">FAQ</a>
                                <a href="#/contact">Contact Us</a>
                            </div>
                        </div>
                    </div>

                    {/* --- Map & UI Controls --- */}
                    <div id="mapWrapper">
                        <TimeSliderControls
                            fhour={fhour}
                            setFhour={setFhour}
                            inputValue={inputValue}
                            setInputValue={setInputValue}
                            play={play}
                            setPlay={setPlay}
                            timeZone={timeZone}
                            setTimeZone={setTimeZone}
                            timeZoneOptions={timeZoneOptions}
                            initstr={initstr}
                            timing={timing}
                            validstr={validstr}
                            timeMinimized={timeMinimized}
                            toggleMinimizeTime={toggleMinimizeTime}
                        />

                        <MapDisplay
                            compareBool={compareBool}
                            lockState={lockState}
                            setLockState={setLockState}
                            gridModels={gridModels}
                            handleGridModelChange={(e, index, models) => {
                              resetHurricaneForModelChange(index);
                              handleGridModelChange(e, index, models, setGridModels);
                            }}
                            griddedMapRefs={griddedMapRefs}
                            disablePopup={disablePopup}
                            enablePopup={enablePopup}
                        />

                        <ColorbarContainer
                            colorMinimized={colorMinimized}
                            toggleMinimizeColor={toggleMinimizeColor}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Home;
